/**
 * Created by neo on 14.09.23
 */
import { makeAutoObservable } from 'mobx';
import { ExploreEntry } from '../Explore/ExploreEntry';
import { Recipe, createRecipe } from '../Diet/Recipe/Recipe';
import { FitnessProgramTemplate, createFitnessProgramTemplate } from '../Coach/Program/Template/FitnessProgramTemplate';
import { buildExploreEntry } from '../Explore/ExploreEntryBuilder';
import { HttpBackend } from '../../Services/Http/HttpBackend';

const categoryOrder = ['nutrition', 'mental', 'movement'];

export type WeaviateSuggestionResult = {
  contentId: string;
  contentType: string;
  tags: string[];
  category: string;
  content?: ExploreEntry | Recipe | FitnessProgramTemplate;
  customMessage?: string;
  personalizedReason?: string;
  link?: string;
  readonly isCustom: boolean;
  readonly recipe?: Recipe;
  readonly exploreEntry?: ExploreEntry;
  readonly fitnessProgramTemplate?: FitnessProgramTemplate;
  readonly fitnessProgramTemplateOrExploreEntry?: FitnessProgramTemplate | ExploreEntry;
};

function buildSuggestionContent(
  contentType: string,
  json?: any,
): ExploreEntry | Recipe | FitnessProgramTemplate | undefined {
  switch (contentType) {
    case 'explore':
      return buildExploreEntry(json);
    case 'recipe':
      return createRecipe(json);
    case 'fitnessProgram':
      return createFitnessProgramTemplate(json);
  }
}

export function createWeaviateSuggestionResult(json?: Partial<WeaviateSuggestionResult>): WeaviateSuggestionResult {
  const contentType = json?.contentType ?? '';
  return makeAutoObservable({
    contentId: json?.contentId ?? '',
    contentType,
    tags: json?.tags ?? [],
    category: json?.category ?? '',
    content: buildSuggestionContent(contentType, (json as any)?.content),
    customMessage: json?.customMessage,
    personalizedReason: json?.personalizedReason,
    link: json?.link,

    get isCustom(): boolean {
      return this.contentType === 'custom' || (!this.content && !!this.customMessage);
    },

    get recipe(): Recipe | undefined {
      return this.contentType === 'recipe' ? (this.content as Recipe) : undefined;
    },

    get exploreEntry(): ExploreEntry | undefined {
      return this.contentType === 'explore' ? (this.content as ExploreEntry) : undefined;
    },

    get fitnessProgramTemplate(): FitnessProgramTemplate | undefined {
      return this.contentType === 'fitnessProgram' ? (this.content as FitnessProgramTemplate) : undefined;
    },

    get fitnessProgramTemplateOrExploreEntry(): FitnessProgramTemplate | ExploreEntry | undefined {
      return this.exploreEntry || this.fitnessProgramTemplate;
    },
  });
}

export const WeaviateSuggestionResultApi = {
  daily(): Promise<WeaviateSuggestionResult[]> {
    return HttpBackend.get('/coach/suggestions/ai/daily')
      .then((result) => (result ?? []).map((res: any) => createWeaviateSuggestionResult(res)))
      .then((res) => res.sort((a, b) => categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category)));
  },

  weekly(): Promise<WeaviateSuggestionResult[]> {
    return HttpBackend.get('/coach/suggestions/ai/weekly')
      .then((result) => (result ?? []).map((res: any) => createWeaviateSuggestionResult(res)))
      .then((res) => res.sort((a, b) => categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category)));
  },

  shortBreaks(): Promise<WeaviateSuggestionResult[]> {
    return HttpBackend.get('/coach/suggestions/ai/shortBreaks')
      .then((result) => (result ?? []).map((res: any) => createWeaviateSuggestionResult(res)))
      .then((res) => res.sort((a, b) => categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category)));
  },

  refresh(category: string): Promise<WeaviateSuggestionResult> {
    return HttpBackend.post(`/coach/suggestions/ai/daily/${category}/refresh`).then((res) =>
      createWeaviateSuggestionResult(res),
    );
  },
};
